/**
 * `src/platform/visible-text.ts`: the visible view of a text, so a secret rule reads across an invisible
 * code point (plan 2026-10-24-aws-dp-identity-and-lifecycle, WS-14, review rows WS-14-P2-R25, R28, R32
 * and R42).
 *
 * An invisible code point can sit anywhere in a secret: inside its prefix (`GOC<U+200B>SPX-`), between
 * the prefix and the value, inside the value, or around the dots of a JWT. A rule that lists each place
 * misses the next one. So the rules read the visible view: the text with each invisible part removed,
 * where an invisible part is one of these:
 *
 * - an escape token `<U+XXXX>` of `displayUntrusted` (4 to 6 upper-case hex digits), the form that the
 *   display scrub reads after its escape step;
 * - a raw code point of the escape class except the layout characters: a control (`\p{Cc}`) other than
 *   `\t`, `\n` and `\r`, a format character (`\p{Cf}`), a line or paragraph separator (`\p{Zl}`,
 *   `\p{Zp}`), or a blank-looking mark (U+034F, U+FE00 to U+FE0F, U+115F, U+1160, U+3164, U+FFA0,
 *   U+2800);
 * - any other default-ignorable code point (`\p{Default_Ignorable_Code_Point}`), such as a variation
 *   selector of the supplement U+E0100 to U+E01EF, which a browser draws as nothing (review rows
 *   WS-14-P3-R15 and R57).
 *
 * `\t`, `\n`, `\r` and a space stay in the view: they are layout, and the console shows them as a space,
 * so they split a secret in the view as they split it on screen. A finder that must read across a line
 * break (a key that a server wrapped) asks for the joined view with `joinLines`, which also removes `\n`
 * and `\r` (review row WS-14-P3-R50).
 *
 * The view keeps where it removed a part: `boundaryBefore(index)` is `true` for a visible code unit that
 * an invisible part preceded. A rule that needs a separator or a word boundary reads it, so an invisible
 * code point in place of the space after `Bearer`, or before `eyJ`, still separates (review rows
 * WS-14-P3-R56, R58 and R59).
 *
 * Callers: `redactSelfNamedCredentials` and the display scrub of `untrusted-text.ts`, and
 * `maskCredentialShapes` of `credential-mask.ts`. Each one finds its ranges on the view, and
 * {@link replaceVisibleRanges} replaces each range of the original text, the invisible parts inside the
 * range included. So no part of a matched secret stays on screen, and the text outside each range keeps
 * every code point it had.
 */

/** One invisible part: an escape token, or a raw invisible code point of the header's list. */
const INVISIBLE_PART = /<U\+[0-9A-F]{4,6}>|[^\P{Cc}\t\n\r]|[\p{Cf}\p{Zl}\p{Zp}\p{Default_Ignorable_Code_Point}͏︀-️ᅟᅠㅤﾠ⠀]/gu;

/** A half-open range `[start, end)` of code-unit indices in the visible view. */
export interface VisibleRange {
  readonly start: number;
  readonly end: number;
}

/** A line break of the joined view: `\n` or `\r`. */
const LINE_BREAK = /[\n\r]/g;

/** The options of {@link replaceVisibleRanges}. */
export interface VisibleViewOptions {
  /** Also remove `\n` and `\r`, so a finder reads across a line break. Default `false`. */
  readonly joinLines?: boolean;
}

/**
 * What a finder learns of the view beside its text: `boundaryBefore(index)` is `true` when the view
 * removed a part right before the visible code unit `index`. `false` for index 0 with nothing removed
 * before it, and for any index outside the view.
 */
export interface VisibleBoundaries {
  readonly boundaryBefore: (index: number) => boolean;
}

/** The visible view of a text: the kept code units, the index in the original text of each one, and the boundaries. */
interface VisibleView {
  readonly text: string;
  readonly origin: readonly number[];
  readonly boundaries: VisibleBoundaries;
}

/** The visible view of `text`. Linear: one global scan of {@link INVISIBLE_PART}, and one of `\n` and `\r` with `joinLines`. */
function visibleView(text: string, options: VisibleViewOptions): VisibleView {
  const pattern = options.joinLines === true ? new RegExp(`${INVISIBLE_PART.source}|${LINE_BREAK.source}`, 'gu') : INVISIBLE_PART;
  let kept = '';
  const origin: number[] = [];
  /* The view index after each removed part, ascending; a run of parts adds its index once. */
  const cuts: number[] = [];
  let cursor = 0;
  const keep = (end: number): void => {
    kept += text.slice(cursor, end);
    for (let index = cursor; index < end; index += 1) origin.push(index);
  };
  for (const part of text.matchAll(pattern)) {
    keep(part.index);
    if (cuts[cuts.length - 1] !== origin.length) cuts.push(origin.length);
    cursor = part.index + part[0].length;
  }
  keep(text.length);
  return { text: kept, origin, boundaries: { boundaryBefore: (index) => hasSorted(cuts, index) } };
}

/** `true` when the ascending array `sorted` holds `value`. A binary search. Pure. */
function hasSorted(sorted: readonly number[], value: number): boolean {
  let low = 0;
  let high = sorted.length - 1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    const at = sorted[middle] as number;
    if (at === value) return true;
    if (at < value) low = middle + 1;
    else high = middle - 1;
  }
  return false;
}

/**
 * `text` with each range that `find` returns on its visible view replaced by `replacement`.
 *
 * Input: any string, a finder that reads the visible view (the text with each invisible part removed,
 * and each line break too with `joinLines`) and its {@link VisibleBoundaries}, and returns ranges on it. A range with `end <= start`, or outside the view, is a programmer
 * error and throws a `RangeError`. Two ranges that overlap become their union; two that only touch stay
 * two replacements.
 *
 * Output: the original text with each range replaced, from the original index of its first visible code
 * unit to the original index after its last visible code unit. An invisible part inside a range goes with
 * it; one before the first or after the last visible code unit of a range stays. With no range, the text
 * comes back unchanged. Pure, and linear in the length of `text` plus the work of `find`.
 */
export function replaceVisibleRanges(
  text: string,
  find: (visible: string, boundaries: VisibleBoundaries) => readonly VisibleRange[],
  replacement: string,
  options: VisibleViewOptions = {},
): string {
  const view = visibleView(text, options);
  const ranges = [...find(view.text, view.boundaries)].sort((a, b) => a.start - b.start);
  let out = '';
  let cursor = 0;
  let open: VisibleRange | null = null;
  const flush = (range: VisibleRange): void => {
    const start = view.origin[range.start] as number;
    out += text.slice(cursor, start) + replacement;
    cursor = (view.origin[range.end - 1] as number) + 1;
  };
  for (const range of ranges) {
    if (range.end <= range.start || range.start < 0 || range.end > view.text.length) {
      throw new RangeError(`replaceVisibleRanges: the range [${range.start}, ${range.end}) is outside the view of ${view.text.length}`);
    }
    if (open !== null && range.start < open.end) {
      open = { start: open.start, end: Math.max(open.end, range.end) };
      continue;
    }
    if (open !== null) flush(open);
    open = range;
  }
  if (open !== null) flush(open);
  return out + text.slice(cursor);
}

/**
 * Every match of the global pattern `pattern` on `visible` as a range, or the range of its capture group
 * `group` when the caller keeps the rest of the match (the `Bearer ` prefix of P3). A match whose group
 * did not take part, or matched no character, gives no range. A `group` on a pattern without the `d`
 * flag is a programmer error and throws a `RangeError`. Pure.
 */
export function matchRanges(visible: string, pattern: RegExp, group?: number): VisibleRange[] {
  if (group !== undefined && !pattern.hasIndices) throw new RangeError(`matchRanges: the pattern ${pattern} needs the d flag for group ${group}`);
  const ranges: VisibleRange[] = [];
  for (const match of visible.matchAll(pattern)) {
    const span = group === undefined ? [match.index, match.index + match[0].length] : (match.indices as RegExpIndicesArray)[group];
    if (span !== undefined && span[1] > span[0]) ranges.push({ start: span[0], end: span[1] });
  }
  return ranges;
}
